import { get } from 'lodash';
import { CALL_PRIVATE_API } from 'js/constants/api';
import * as actionTypes from '../../constants/actionTypes';
import { POST } from '../../constants/requestMethods';
import { RESOURCE_TYPE_SOURCE } from '../../constants/resourceTypes';
import { setReferencedResourceIds } from '../../lib/ResourceUtils';

const FILE_UPLOAD_PROBE_PLACEHOLDER_ENDPOINT = '/file_upload/probe/sample';

export const probeGetConnectionTree =
  (id, isRoot = false, info = null) =>
  (dispatch, getState) => {
    let {
      entities: { credentials },
    } = getState();
    let connectionType = get(credentials, `['${id}'].credentials_type`);
    let endpoint = `/data_credentials/${id}/probe/tree`;
    let payload = { depth: 1 };
    let key;

    if (!isRoot && info) {
      if (info.type === 'database') {
        payload.database = info.name;
        key = info.id;
      } else if (info.type === 'table') {
        payload.database = info.parent;
        payload.table = info.name;
        key = info.id;
      } else if (info.fullPath || info.path) {
        payload.path = info.fullPath || info.path;
        key = payload.path;
      }
    }

    // dedup guard: skip if a request for this credential+node is already in-flight.
    const loadingState = getState().probe?.loading || {};
    const loadingKey = key || id;
    if (loadingState[loadingKey]) return Promise.resolve();

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.PROBE_GET_TREE_REQUEST,
          actionTypes.PROBE_GET_TREE_SUCCESS,
          actionTypes.PROBE_GET_TREE_FAILURE,
        ],
        endpoint,
        schema: null,
        requestMethod: POST,
        payload,
        requestInfo: {
          id,
          isRoot,
          info,
          key,
          connectionType,
        },
      },
    });
  };

export const probeGetInitialDBTree =
  (id, isRoot = false, info = null) =>
  (dispatch, getState) => {
    const tableId = info.parent + '/' + info.name;

    return dispatch(probeGetConnectionTree(id, isRoot))
      .then(() => {
        const probeTree = getState().probe[id].tree;
        const tableNode = probeTree[tableId];

        if (!tableNode) {
          const databaseId = info.parent;
          const databaseNode = probeTree[databaseId];

          return dispatch(probeGetConnectionTree(id, false, databaseNode));
        }
      })
      .then(() => {
        const probeTree = getState().probe[id].tree;
        const tableNode = probeTree[tableId];

        if (tableNode) {
          return dispatch(probeGetConnectionTree(id, false, tableNode));
        }
      });
  };

export const probeGetWSDL = (id, wsdlURL) => (dispatch) => {
  let endpoint = `/data_credentials/${id}/probe/tree`;
  let payload = {
    'soap.wsdl.url': wsdlURL,
  };

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.PROBE_GET_WSDL_REQUEST,
        actionTypes.PROBE_GET_WSDL_SUCCESS,
        actionTypes.PROBE_GET_WSDL_FAILURE,
      ],
      endpoint,
      schema: null,
      requestMethod: POST,
      payload,
      requestInfo: {
        id,
      },
    },
  });
};

export const probeGetKafkaTopics = (id) => (dispatch) => {
  let endpoint = `/data_credentials/${id}/probe/tree`;
  let payload = {};

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.PROBE_GET_KAFKA_TOPICS_REQUEST,
        actionTypes.PROBE_GET_KAFKA_TOPICS_SUCCESS,
        actionTypes.PROBE_GET_KAFKA_TOPICS_FAILURE,
      ],
      endpoint,
      schema: null,
      requestMethod: POST,
      payload,
      requestInfo: {
        id,
      },
    },
  });
};

export const probeCallSOAP = (id, payload) => (dispatch) => {
  let endpoint = `/data_credentials/${id}/probe/sample`;

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.PROBE_CALL_SOAP_REQUEST,
        actionTypes.PROBE_CALL_SOAP_SUCCESS,
        actionTypes.PROBE_CALL_SOAP_FAILURE,
      ],
      endpoint,
      schema: null,
      requestMethod: POST,
      payload,
      requestInfo: {
        id,
      },
    },
  });
};

export const probeGetFile =
  (
    id,
    info = null,
    selectedConnection = null,
    shouldFetchOnResource = false,
    resourceType = null,
    resourceId = null,
  ) =>
  (dispatch) => {
    let endpoint = `/data_credentials/${id}/probe/sample`;
    let payload = {};

    if (shouldFetchOnResource === true) {
      switch (resourceType) {
        case RESOURCE_TYPE_SOURCE:
          endpoint = `/data_sources/${resourceId}/probe/sample`;
          break;
        default:
          endpoint = '';
      }

      if (info && info.name) {
        payload.path = info.name;
      }
    } else if (info && info.path) {
      payload.path = info.path;
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.PROBE_GET_FILE_REQUEST,
          actionTypes.PROBE_GET_FILE_SUCCESS,
          actionTypes.PROBE_GET_FILE_FAILURE,
        ],
        endpoint,
        schema: null,
        requestMethod: POST,
        payload,
        requestInfo: {
          id,
          info,
          shouldFetchOnResource,
          resourceType,
          resourceId,
        },
      },
    });
  };

export const probeToggleTreeNode = (id, node) => {
  return {
    type: actionTypes.PROBE_TOGGLE_NODE,
    payload: {
      id,
      node,
    },
  };
};

export const probeGetSample = (id, stageIndex, payload) => (dispatch) => {
  payload = setReferencedResourceIds(payload);

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.PROBE_GET_SAMPLE_REQUEST,
        actionTypes.PROBE_GET_SAMPLE_SUCCESS,
        actionTypes.PROBE_GET_SAMPLE_FAILURE,
      ],
      endpoint: `/data_credentials/${id}/probe/sample`,
      requestMethod: POST,
      payload,
      requestInfo: {
        id,
        stageIndex,
      },
    },
  });
};

/**
 * File Upload-specific probe action.
 * Used at File Upload connector.
 * Uses an isolated endpoint + reducer flow.
 * There is no interference on S3 connector
 */
export const fileUploadProbeGetSampleWithFile = (id, stageIndex, file, payload) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.FILE_UPLOAD_PROBE_GET_SAMPLE_REQUEST,
        actionTypes.FILE_UPLOAD_PROBE_GET_SAMPLE_SUCCESS,
        actionTypes.FILE_UPLOAD_PROBE_GET_SAMPLE_FAILURE,
      ],
      // TODO(file-upload-probe): replace placeholder endpoint when backend contract is finalized.
      endpoint: FILE_UPLOAD_PROBE_PLACEHOLDER_ENDPOINT,
      requestMethod: POST,
      isMultipart: true,
      payload: {
        file,
        config: JSON.stringify(setReferencedResourceIds(payload)),
      },
      requestInfo: {
        id,
        stageIndex,
      },
    },
  });
};

export const probeGetKafkaTopicSample =
  (id, topic, topicFieldName, sourceConfig = {}) =>
  (dispatch) => {
    let payload = {
      [topicFieldName]: topic,
    };
    payload = {
      ...payload,
      ...sourceConfig,
    };

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.PROBE_GET_KAFKA_TOPIC_SAMPLE_REQUEST,
          actionTypes.PROBE_GET_KAFKA_TOPIC_SAMPLE_SUCCESS,
          actionTypes.PROBE_GET_KAFKA_TOPIC_SAMPLE_FAILURE,
        ],
        endpoint: `/data_credentials/${id}/probe/sample`,
        requestMethod: POST,
        payload,
        requestInfo: {
          id,
          topic,
        },
      },
    });
  };

export const probeClearAll = () => {
  return {
    type: actionTypes.PROBE_CLEAR_ALL,
  };
};

export const probeRemoveRestStage = (id, stageIndex) => {
  return {
    type: actionTypes.PROBE_REMOVE_REST_STAGE,
    payload: {
      id,
      stageIndex,
    },
  };
};

export const fileUploadProbeRemoveStage = (id, stageIndex) => {
  return {
    type: actionTypes.FILE_UPLOAD_PROBE_REMOVE_STAGE,
    payload: {
      id,
      stageIndex,
    },
  };
};

export const probeClearFiles = (
  credId,
  shouldFetchOnResource = false,
  resourceType = null,
  resourceId = null,
) => {
  return {
    type: actionTypes.PROBE_CLEAR_FILES,
    payload: {
      id: credId,
      shouldFetchOnResource,
      resourceType,
      resourceId,
    },
  };
};

export const testDataset =
  ({ id, sampleData, sinkConfig, templateConfig, vendorEndpointId, nexlaMetaData, resourceType }) =>
  (dispatch) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.TEST_DATASET_REQUEST,
          actionTypes.TEST_DATASET_SUCCESS,
          actionTypes.TEST_DATASET_FAILURE,
        ],
        endpoint: `/data_credentials/${id}/probe/sample`,
        schema: null,
        requestMethod: POST,
        payload: {
          ...(templateConfig && {
            template_config: templateConfig,
          }),
          ...(sinkConfig && {
            sink_config: sinkConfig,
          }),
          ...(vendorEndpointId && { vendor_endpoint_id: vendorEndpointId }),
          ...(resourceType && { resource_type: resourceType }),
          input: [
            {
              nexlaMetaData,
              rawMessage: sampleData,
            },
          ],
        },
      },
    });
  };
